import React, { useMemo } from "react";
import { CalendarHeart, Cake, Heart, Droplets, ChevronRight } from "lucide-react";
import CollapsibleCard from "@/components/ui/CollapsibleCard";
import ListItem from "@/components/ui/ListItem";
import { getYearsSince, formatSmartDate } from "@/lib/date";

function getFirstName(fullName) {
  return fullName?.split(" ")[0] ?? fullName ?? "";
}

function daysUntil(dateStr) {
  if (!dateStr) return null;
  const parts = dateStr.split("-").map(Number);
  if (parts.length < 3) return null;
  const [, month, day] = parts;
  const now = new Date();
  const todayMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const thisYear = new Date(todayMidnight.getFullYear(), month - 1, day);
  const next = thisYear >= todayMidnight ? thisYear : new Date(todayMidnight.getFullYear() + 1, month - 1, day);
  return Math.round((next - todayMidnight) / 86400000);
}

function countdownLabel(days) {
  if (days === null) return null;
  if (days === 0) return "es hoy";
  if (days === 1) return "mañana";
  return `faltan ${days} días`;
}

// daysAway > 0 means the event hasn't occurred yet this cycle — show the age
// they WILL TURN, not the one they currently have.
function birthdayAge(dateStr, daysAway) {
  if (!dateStr) return null;
  const years = getYearsSince(dateStr);
  if (years === null) return null;
  const n = daysAway > 0 ? years + 1 : years;
  return `${n} ${n === 1 ? "año" : "años"}`;
}

function baptismYearsSimple(dateStr, daysAway) {
  if (!dateStr) return null;
  const years = getYearsSince(dateStr);
  if (years === null) return null;
  const n = daysAway > 0 ? years + 1 : years;
  if (n === 0) return null;
  return `${n} ${n === 1 ? "año" : "años"}`;
}

function marriageYearsSimple(dateStr, daysAway) {
  if (!dateStr) return null;
  const years = getYearsSince(dateStr);
  if (years === null) return null;
  const n = daysAway > 0 ? years + 1 : years;
  return `${n} ${n === 1 ? "año" : "años"}`;
}

function birthdaySubtitle(dateStr) {
  const days = daysUntil(dateStr);
  const age = birthdayAge(dateStr, days);
  if (days === 0) return ["Hoy", age].filter(Boolean).join(" · ");
  return [formatSmartDate(dateStr), countdownLabel(days), age].filter(Boolean).join(" · ");
}

function ImportantDates({
  birthdaysToday = [],
  birthdaysUpcoming,
  anniversariesToday = [],
  anniversariesUpcoming,
  baptismsUpcoming = [],
  families = [],
  navigate,
}) {
  const familyMap = useMemo(
    () => Object.fromEntries(families.map((f) => [f.id, f.name])),
    [families]
  );

  // Merge today's birthdays + upcoming, deduped, sorted chronologically
  const allBirthdays = useMemo(() => {
    const seen = new Set();
    const result = [];
    for (const p of [...birthdaysToday, ...birthdaysUpcoming]) {
      if (!seen.has(p.id)) { seen.add(p.id); result.push(p); }
    }
    return result
      .sort((a, b) => {
        const da = daysUntil(a.birth_date ?? a.birthdate) ?? Infinity;
        const db = daysUntil(b.birth_date ?? b.birthdate) ?? Infinity;
        return da - db;
      })
      .slice(0, 6);
  }, [birthdaysToday, birthdaysUpcoming]);

  // Merge today's anniversaries + upcoming, deduped by id (same pattern as allBirthdays)
  const allAnniversaries = useMemo(() => {
    const seen = new Set();
    const result = [];
    for (const p of [...anniversariesToday, ...anniversariesUpcoming]) {
      if (!seen.has(p.id)) { seen.add(p.id); result.push(p); }
    }
    return result;
  }, [anniversariesToday, anniversariesUpcoming]);

  // Group anniversaries by family_id + marriage_date so couples appear as one item
  const groupedAnniversaries = useMemo(() => {
    const coupleMap = {};
    const solo = [];

    for (const p of allAnniversaries) {
      const dateKey = p.marriage_date?.slice(0, 10);
      if (p.family_id && dateKey) {
        const key = `${p.family_id}_${dateKey}`;
        if (!coupleMap[key]) coupleMap[key] = [];
        coupleMap[key].push(p);
      } else {
        solo.push({ type: "solo", person: p });
      }
    }

    const result = [];
    for (const members of Object.values(coupleMap)) {
      if (members.length >= 2) {
        result.push({ type: "couple", members });
      } else {
        result.push({ type: "solo", person: members[0] });
      }
    }

    const getAnnivDays = (entry) => {
      const date = entry.type === "couple"
        ? entry.members[0]?.marriage_date
        : entry.person?.marriage_date;
      return daysUntil(date) ?? Infinity;
    };

    return [...result, ...solo]
      .sort((a, b) => getAnnivDays(a) - getAnnivDays(b))
      .slice(0, 5);
  }, [allAnniversaries]);

  // Baptisms sorted chronologically
  const sortedBaptisms = useMemo(
    () =>
      [...baptismsUpcoming]
        .sort((a, b) => (daysUntil(a.baptism_date) ?? Infinity) - (daysUntil(b.baptism_date) ?? Infinity))
        .slice(0, 5),
    [baptismsUpcoming]
  );

  const hasContent =
    allBirthdays.length > 0 ||
    groupedAnniversaries.length > 0 ||
    sortedBaptisms.length > 0;

  return (
    <div className="px-4 pb-2">

      <CollapsibleCard
        defaultOpen={false}
        title={
          <span className="flex items-center gap-2">
            <CalendarHeart size={15} className="text-rose-400" />
            Cumpleaños y aniversarios
          </span>
        }
        empty={!hasContent}
        emptyText="Sin fechas importantes próximas"
      >

        {/* ── 1. CUMPLEAÑOS ───────────────────────────────── */}
        {allBirthdays.map((p) => {
          const dateStr = p.birth_date ?? p.birthdate;
          return (
            <ListItem
              key={`bu-${p.id}`}
              icon={<Cake size={16} className="text-pink-500" />}
              title={p.name}
              subtitle={birthdaySubtitle(dateStr)}
              onClick={() => navigate(`/people/${p.id}`)}
            />
          );
        })}

        {/* ── 2. ANIVERSARIOS DE BODAS ────────────────────── */}
        {groupedAnniversaries.map((entry, idx) => {
          if (entry.type === "couple") {
            const [p1, p2] = entry.members;
            const nombre2Imp  = getFirstName(p2.name);
            const conectorImp = nombre2Imp.match(/^[IiÍí]/) ? "e" : "y";
            const coupleTitle = `${getFirstName(p1.name)} ${conectorImp} ${nombre2Imp}`;
            const familyName = familyMap[p1.family_id];
            const days = daysUntil(p1.marriage_date);
            const dateCountdownYears = [
              formatSmartDate(p1.marriage_date),
              countdownLabel(days),
              marriageYearsSimple(p1.marriage_date, days),
            ].filter(Boolean).join(" · ");

            return (
              <button
                key={`ac-${p1.family_id}-${idx}`}
                onClick={() => navigate(`/families/${p1.family_id}`)}
                className="
                  group w-full text-left
                  rounded-[20px] border border-slate-200/70 dark:border-slate-700/60
                  bg-white dark:bg-slate-800
                  p-3.5 shadow-sm
                  transition-[transform,box-shadow,border-color] duration-200 ease-out
                  hover:shadow-md hover:border-slate-300/60 dark:hover:border-slate-600/60
                  active:scale-[0.98]
                "
              >
                <div className="flex items-start gap-3">
                  <div className="h-11 w-11 shrink-0 rounded-[14px] bg-rose-50 dark:bg-rose-900/20 border border-rose-100 dark:border-rose-800/30 flex items-center justify-center mt-0.5">
                    <Heart size={16} className="text-rose-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[14px] font-semibold tracking-tight text-slate-900 dark:text-slate-100 truncate leading-snug">
                      {coupleTitle}
                    </p>
                    {familyName && (
                      <p className="mt-0.5 text-[11px] font-medium text-slate-600 dark:text-slate-300 truncate leading-snug">
                        {familyName}
                      </p>
                    )}
                    {dateCountdownYears && (
                      <p className="mt-0.5 text-[11px] text-slate-400 dark:text-slate-500 truncate leading-snug">
                        {dateCountdownYears}
                      </p>
                    )}
                  </div>
                  <ChevronRight
                    size={16}
                    className="
                      shrink-0 mt-0.5
                      text-slate-300/70 dark:text-slate-600
                      transition-[transform,color] duration-200 ease-out
                      group-hover:text-slate-500 dark:group-hover:text-slate-400
                      group-hover:translate-x-0.5
                    "
                  />
                </div>
              </button>
            );
          }

          const p = entry.person;
          const soloAnnivDays = daysUntil(p.marriage_date);
          const combined = [
            formatSmartDate(p.marriage_date),
            countdownLabel(soloAnnivDays),
            marriageYearsSimple(p.marriage_date, soloAnnivDays),
          ].filter(Boolean).join(" · ");

          return (
            <ListItem
              key={`as-${p.id}`}
              icon={<Heart size={16} className="text-rose-500" />}
              title={p.name}
              subtitle={combined}
              onClick={() =>
                navigate(p.family_id ? `/families/${p.family_id}` : `/people/${p.id}`)
              }
            />
          );
        })}

        {/* ── 3. ANIVERSARIOS DE BAUTISMO ─────────────────── */}
        {sortedBaptisms.map((p) => {
          const days = daysUntil(p.baptism_date);
          const subtitle = [
            days === 0 ? "Hoy" : formatSmartDate(p.baptism_date),
            days === 0 ? null : countdownLabel(days),
            baptismYearsSimple(p.baptism_date, days),
          ].filter(Boolean).join(" · ");
          return (
            <ListItem
              key={`bap-${p.id}`}
              icon={<Droplets size={16} className="text-teal-500 dark:text-teal-400" />}
              title={p.name}
              subtitle={subtitle}
              onClick={() => navigate(`/people/${p.id}`)}
            />
          );
        })}

      </CollapsibleCard>

    </div>
  );
}

export default React.memo(ImportantDates);
